import React, { FC, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { actionUpdateReportCv } from '../../../../redux/action/userSingleReport/actionUserSingleReport';
import { formatReportData } from '../../../../utils/formatReportData';
import { AppDispatch } from '../../../../redux/reducer/store';
import { IReportSkuCvOptions } from '../../../../types/reportsInterfaces';

import Select from '../../../custom/select/select';
import SelectPopup from '../../../custom/selectPopup/selectPopup';
import EditPopup from '../../../custom/editPopup/editPopup';
import ArrowIcon from '../../../../assets/icons/chevron.svg';
import { IUserOptions } from '../../../../types/interfaces';

const getCvValueType = (elem: IReportSkuCvOptions) =>
	Object.values(elem.type).join('');

const isCvValueSelectType = (type: string) =>
	type === 'Фото' || type === 'Да/Нет' || type === 'Справочник';

const getCvValueSelectOptions = (elem: IReportSkuCvOptions) => {
	const type = getCvValueType(elem);

	if (type === 'Фото') {
		return [
			{ value: 'Сделано', label: 'Сделано' },
			{ value: 'Не сделано', label: 'Не сделано' },
		];
	}
	if (type === 'Да/Нет') {
		return [
			{ value: 'Да', label: 'Да' },
			{ value: 'Нет', label: 'Нет' },
		];
	}
	if (type === 'Справочник') {
		return (elem.dictionaryValues ?? []).map(({ name }) => ({
			value: name,
			label: name,
		}));
	}
	return [];
};

const CvTableSection: FC = () => {
	const dispatch = useDispatch<AppDispatch>();

	const { report } = useSelector(
		(state: any) => state.userSingleReport.userSingleReport
	);
	const { claims } = useSelector((state: IUserOptions) => state.user.user.data);

	const [openOutletDataIndex, setOpenOutletDataIndex] = useState<number>(-1);
	const [newCvValue, setNewCvValue] = useState<string>('');
	const [isVisibleData, setIsVisibleData] = useState<boolean>(true);

	const modalRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		document.addEventListener('mousedown', handleDocumentClick);

		return () => {
			document.removeEventListener('mousedown', handleDocumentClick);
		};
	}, []);

	const handleClickCancelRenameBtn = () => {
		setOpenOutletDataIndex(-1);
	};

	const handleChangeReportData = (index: number, section: string) => {
		const sectionToStateMap: any = {
			outletData: {
				setState: setOpenOutletDataIndex,
				state: openOutletDataIndex,
			},
		};

		const setOpenIndexFunction = sectionToStateMap[section];

		if (setOpenIndexFunction) {
			if (index === setOpenIndexFunction.state) {
				return;
			} else {
				setOpenIndexFunction.setState(index);
			}
		}
	};

	const handleAcceptRenameCv = (elem: IReportSkuCvOptions, typeOfValue: string) => {
		dispatch(
			actionUpdateReportCv({
				reportId: report && report.reportId.id,
				data: [
					{
						name: elem.name,
						value:
							typeOfValue === 'value' ? newCvValue || elem.value : elem.value,
						hotline: elem.hotline,
					},
				],
			})
		);

		handleClickCancelRenameBtn();
		setNewCvValue('');
	};

	const handleHotlineSelectChange = (
		selectedOption: { value: string; label: string },
		elem: IReportSkuCvOptions
	) => {
		dispatch(
			actionUpdateReportCv({
				reportId: report && report.reportId.id,
				data: [
					{
						name: elem.name,
						value: elem.value,
						hotline: selectedOption.value,
					},
				],
			})
		);
	};

	const handleAcceptEditOnKeyPress = (event: React.KeyboardEvent<HTMLInputElement>, elem: IReportSkuCvOptions, type: string) => {
		if (event.key === 'Enter') {
			handleAcceptRenameCv(elem, type);
		}
	};

	const handleDocumentClick = (event: MouseEvent) => {
		const target = event.target as Element;
		if (
			target.closest('[role="listbox"]') ||
			target.closest('[role="option"]')
		) {
			return;
		}
		if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
			setOpenOutletDataIndex(-1);
		}
	};

	const sortedCv: IReportSkuCvOptions[] =
		report?.cv
			?.slice()
			?.sort((a: IReportSkuCvOptions, b: IReportSkuCvOptions) =>
				a.name?.localeCompare(b.name)
			) ?? [];

	const hasAnyWithPlan = sortedCv.some(
		(elem) => elem.category.DisplayWithPlan === 'Выкладка (с планом)'
	);

	return (
		<div className={`sectionData ${isVisibleData ? 'sectionData_active  sectionData__overflow' : ''}`}>
			<span className='section__title'>Данные по ТТ</span>
			<button
				className='sectionData_button'
				onClick={() => setIsVisibleData(!isVisibleData)}
			>
				<img src={ArrowIcon} alt="Custom Indicator" className='icon' />
			</button>
			<table className='customer__skus_table'>
				<thead className='tunder__thead'>
					<tr>
						<th>Наименование</th>
						{hasAnyWithPlan && <th>План</th>}
						<th>Значение</th>
						<th>Горячая линия</th>
					</tr>
				</thead>
				<tbody>
					{report &&
						sortedCv.map((elem: IReportSkuCvOptions, index: number) => {
								const cvValueType = getCvValueType(elem);

								return (
									<tr key={index}>
										<td>{elem.name}</td>
										{hasAnyWithPlan && (
											<td>
												{elem.category.DisplayWithPlan === 'Выкладка (с планом)'
													? elem.weightPlan
													: '—'}
											</td>
										)}
										<td
											onClick={() => {
												if (
													claims.includes('admin') ||
													claims.includes('reports:edit')
												) {
													handleChangeReportData(index, 'outletData');
												}
											}}
											className='edit__data_text'
										>
											<div className={
													formatReportData(elem?.changes)?.includes('value')
														? 'changed__table_value'
														: formatReportData(elem?.equals)?.includes('Value')
															? 'previous__table_value'
															: ''
												}>
											<span>
												{elem.value}
											</span>
											</div>

											{openOutletDataIndex === index &&
												(isCvValueSelectType(cvValueType) ? (
													<SelectPopup
														handleClickAcceptBtn={() =>
															handleAcceptRenameCv(elem, 'value')
														}
														handleClickCancelBtn={handleClickCancelRenameBtn}
														handleChangeValue={(selectedOption) => {
															setNewCvValue(selectedOption.value);
														}}
														value={newCvValue}
														defaultValue={elem.value}
														options={getCvValueSelectOptions(elem)}
														modalRef={modalRef}
													/>
												) : (
													<EditPopup
														handleClickAcceptRenameBtn={() => {
															handleAcceptRenameCv(elem, 'value');
														}}
														handleAcceptOnKeyPress={(
															event: React.KeyboardEvent<HTMLInputElement>
														) => handleAcceptEditOnKeyPress(event, elem, 'value')}
														handleClickCancelRenameBtn={handleClickCancelRenameBtn}
														handleChangeValue={(
															event: React.ChangeEvent<HTMLInputElement>
														) => {
															setNewCvValue(event?.target.value);
														}}
														defaultValue={elem.value}
														className='edit__popup'
														modalRef={modalRef}
													/>
												))}
										</td>
										<td style={{ minWidth: 200 }}>
											<Select
												value={{
													value: elem.hotline || '',
													label: elem.hotline || ' ',
												}}
												options={[
													{ value: '', label: '\u00A0' },
													...(report?.cvHotlines?.map((h: string) => ({
														value: h,
														label: h,
													})) || []),
												]}
												name="cvHotlines"
												propsChange={(selectedOption: {
													value: string;
													label: string;
												}) => {
													handleHotlineSelectChange(selectedOption, elem);
												}}
												style={{ width: '100%' }}
												isHotlineChanged={
													elem?.changes &&
													formatReportData(elem?.changes)?.includes('hotline')
												}
											/>
										</td>
									</tr>
								);
							})}
				</tbody>
			</table>
		</div>
	);
};

export default CvTableSection;
